import { memo, useCallback, useEffect, useMemo, useRef } from 'react'
import { Checkbox, IconButton } from '@cargomart/ui'
import { PlaceholderCell } from 'components/extend/PlaceholderCell'
import { FormApi } from 'final-form'
import { Form } from 'forms/Form'
import IconPlus from 'remixicon/icons/System/add-line.svg?react'
import IconMinus from 'remixicon/icons/System/subtract-fill.svg?react'
import IconEmotionUnhappy from 'remixicon/icons/User & Faces/emotion-unhappy-line.svg?react'
import { OperationItem } from 'schema'
import { FieldNumber } from 'shared/forms/FieldNumber'
import { isNumber } from 'shared/helpers/number'

interface FormValues {
  amounts: Record<string, number | null>
  selected: Record<string, boolean>
}

interface IProps {
  formRef: React.MutableRefObject<FormApi<any> | null>
  initialValues: Pick<FormValues, 'amounts'>
  isFetching: boolean
  isLoading: boolean
  maxAmountByOperationId: Record<string, number>
  onSubmit: (values: FormValues) => void
  pointOperations: OperationItem[]
  onValidityChange?: (isValid: boolean) => void
}

const SetFormRef = ({
  form,
  formRef
}: {
  form: FormApi<any>
  formRef: React.MutableRefObject<FormApi<any> | null>
}) => {
  useEffect(() => {
    formRef.current = form

    return () => {
      formRef.current = null
    }
  }, [form, formRef])

  return null
}

const Skeleton = () =>
  [...new Array(5)].map((_, i) => (
    <div key={i} className="mb-2 flex items-start gap-3">
      <PlaceholderCell className="!w-8" height={24} />
      <div className="flex w-full cursor-pointer flex-col">
        <PlaceholderCell className="mb-1" height={24} />
        <PlaceholderCell height={20} />
      </div>
    </div>
  ))

const NotifyValidity = ({
  isValid,
  onValidityChange
}: {
  isValid: boolean
  onValidityChange: (isValid: boolean) => void
}) => {
  useEffect(() => {
    onValidityChange(isValid)
  }, [isValid, onValidityChange])

  return null
}

const toValidAmount = (value: unknown): number | undefined => {
  if (typeof value !== 'number' || !Number.isFinite(value) || !Number.isInteger(value)) return undefined

  return value >= 1 ? value : undefined
}

const parseAmount = (value: unknown) => {
  let num = NaN

  if (typeof value === 'number') {
    num = value
  } else if (typeof value === 'string' && value.trim() !== '') {
    num = Number(value)
  }

  return toValidAmount(num)
}

const isInvalidAmount = (amount: number | null | undefined, maxAmount: number) =>
  amount !== null && (toValidAmount(amount) === undefined || (typeof amount === 'number' && amount > maxAmount))

export const OrderCreatePointOperationContent = memo<IProps>(function OrderCreatePointOperationContent({
  formRef,
  initialValues,
  isFetching,
  isLoading,
  maxAmountByOperationId,
  onSubmit,
  onValidityChange,
  pointOperations
}) {
  const lastValidRef = useRef<Record<string, number>>({})

  const formInitialValues = useMemo(
    () => ({
      amounts: initialValues.amounts,
      selected: Object.fromEntries(Object.keys(initialValues.amounts).map((operationId) => [operationId, true]))
    }),
    [initialValues]
  )

  const handleSubmit = useCallback(
    (values: FormValues) => {
      const amounts: Record<string, number | null> = {}

      pointOperations.forEach((operation) => {
        const operationId = operation.id
        const amount = values.amounts?.[operationId]

        if (typeof amount === 'number' && Number.isFinite(amount) && amount >= 1) {
          amounts[operationId] = amount
        } else if (amount === null) {
          amounts[operationId] = null
        } else if (lastValidRef.current[operationId] !== undefined) {
          amounts[operationId] = lastValidRef.current[operationId]
        }
      })

      onSubmit({ ...values, amounts })
    },
    [onSubmit, pointOperations]
  )

  if (isLoading || isFetching) {
    return (
      <div className="flex select-none flex-col py-4">
        <Skeleton />
      </div>
    )
  }

  if (pointOperations.length === 0) {
    return (
      <div className="flex min-h-[240px] flex-col items-center justify-center py-4 text-center">
        <IconEmotionUnhappy className="mb-4 text-on-surface-variant" containerSize="xl" size="xl" />
        <div className="max-w-80 text-body-lg text-on-surface-variant">
          Дополнительные услуги не&nbsp;предоставляются на&nbsp;этом адресе
        </div>
      </div>
    )
  }

  const fieldClassName =
    'w-[72px] [&>*:first-child]:text-center [&>*:first-child]:!text-on-surface [&>*]:!text-on-surface-variant'

  return (
    <div className="flex select-none flex-col py-4">
      <Form
        enableReinitialize
        initialValues={formInitialValues}
        onSubmit={handleSubmit}
        render={({ form, values }) => {
          const formApi = form as FormApi<any>
          const formValues = values as Partial<FormValues>
          const amounts: Record<string, number | null> = formValues?.amounts ?? {}
          const selected: Record<string, boolean> = formValues?.selected ?? {}

          Object.entries(amounts).forEach(([operationId, amount]) => {
            const valid = toValidAmount(amount)

            if (valid !== undefined) {
              lastValidRef.current[operationId] = valid
            }
          })

          const isFormValid = pointOperations.every((operation) => {
            const operationAmount = amounts[operation.id]
            const isSelected =
              selected[operation.id] === true || (operationAmount !== undefined && operationAmount !== null)
            const maxAmount = maxAmountByOperationId[operation.id] ?? 1

            return !(isSelected && maxAmount > 1) || !isInvalidAmount(operationAmount, maxAmount)
          })

          return (
            <>
              <SetFormRef form={formApi} formRef={formRef} />
              {onValidityChange && <NotifyValidity isValid={isFormValid} onValidityChange={onValidityChange} />}
              {pointOperations.map((operation) => {
                const maxAmount = maxAmountByOperationId[operation.id] ?? 1
                const amount = amounts[operation.id]
                const isSelected = selected[operation.id] === true || (amount !== undefined && amount !== null)

                const handleToggle = () => {
                  if (isSelected) {
                    formApi.change(`selected.${operation.id}`, false)
                    formApi.change(`amounts.${operation.id}`, null)
                  } else {
                    formApi.change(`selected.${operation.id}`, true)
                    formApi.change(`amounts.${operation.id}`, 1)
                  }
                }

                const handleBlur = () => {
                  if (toValidAmount(amount) === undefined) {
                    formApi.change(`amounts.${operation.id}`, lastValidRef.current[operation.id] ?? 1)
                  }
                }

                const handleDecrement = () => {
                  if (!isNumber(amount) || amount <= 1) return

                  formApi.change(`amounts.${operation.id}`, amount - 1)
                }

                const handleIncrement = () => {
                  if (!isNumber(amount) || amount >= maxAmount) return

                  formApi.change(`amounts.${operation.id}`, amount + 1)
                }

                return (
                  <div key={operation.id} className="mb-2 flex items-start gap-3">
                    <Checkbox checked={isSelected} id={`point-modal-${operation.id}`} onChange={handleToggle} />
                    <label className="flex flex-1 cursor-pointer flex-col" htmlFor={`point-modal-${operation.id}`}>
                      <span className="text-label-md leading-6 text-on-surface">{operation.name}</span>
                      {operation.description && (
                        <span className="text-body-sm text-on-surface-variant">
                          {`${operation.description}${operation.unitDescription ? '.' : ''}`}
                        </span>
                      )}
                      {operation.unitDescription && (
                        <span className="text-body-sm text-on-surface-variant">
                          {`Единица услуги: ${operation.unitDescription}${operation.description ? '.' : ''}`}
                        </span>
                      )}
                    </label>
                    {isSelected && maxAmount > 1 && (
                      <div className="grid grid-cols-[1fr_auto_auto] gap-x-1">
                        <IconButton outline disabled={!isNumber(amount) || amount <= 1} onClick={handleDecrement}>
                          <IconMinus size="xs" />
                        </IconButton>
                        <FieldNumber
                          className={fieldClassName}
                          error={isInvalidAmount(amount, maxAmount)}
                          max={maxAmount}
                          min={1}
                          name={`amounts.${operation.id}`}
                          onBlur={handleBlur}
                          parse={parseAmount}
                          postfix="шт"
                        />
                        <IconButton
                          outline
                          disabled={!isNumber(amount) || amount >= maxAmount}
                          onClick={handleIncrement}
                        >
                          <IconPlus size="xs" />
                        </IconButton>
                      </div>
                    )}
                  </div>
                )
              })}
            </>
          )
        }}
      />
    </div>
  )
})
